import { useState, useEffect, useMemo, useRef } from "react";
import { Form } from "antd";
import { getMsg } from "../../utils/antdStatic";
import { adminApi } from "../../services/admin.api";
import { useSidebar } from "../../hooks/useSidebar";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { normalize, matches } from "../../utils/string.utils";
import { clavesCambiadas } from "../../utils/camposCambiados";
import { normalizeRole } from "../../utils/roles.utils";
import { telefonoParaGuardar } from "../../utils/telefonoE164";
import { marcarCampoTelefono } from "../../utils/telefonoErrorApi";
import { irAlPrimerError } from "../../utils/formularioErrores";
import { encontrarTelefonosRepetidos } from "../../utils/telefonosRepetidos";
import type { ErrorApi } from "../../services/apiClient";
import type { AdminUser } from "../../interfaces/api.interfaces";

// Round 2026-04-30 septies — clave 'api-keys' removida; el panel se quitó.
type TabKey =
  | "administradores"
  | "configuracion"
  | "zonas"
  | "imagenes";

/** Cómo se recuerda la pestaña abierta (round 2026-09-24). */
interface OpcionesPestana {
  /**
   * Pestañas que el rol actual PUEDE ver. Una guardada que ya no está en la
   * lista (p.ej. una de superadmin en la sesión de un admin) se descarta: si
   * no, el panel quedaría sin contenido y sin explicación.
   */
  pestanasValidas?: string[];
  /** `false` cuando la URL trae `?tab=`: el enlace directo manda. */
  restaurar?: boolean;
}

export const useAdministradoresLogic = (
  defaultTab: TabKey = "administradores",
  opcionesPestana: OpcionesPestana = {},
) => {
  const [activeTab, setActiveTab] = useFiltroPersistido<TabKey>(
    "configuracion.pestana",
    defaultTab,
    {
      validar: (v) =>
        typeof v === "string" &&
        (opcionesPestana.pestanasValidas ?? []).includes(v),
      restaurar: opcionesPestana.restaurar,
    },
  );

  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<AdminUser | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [form] = Form.useForm();
  // Guardando: bloquea el segundo clic. El `ref` corta el doble clic al instante (el estado se actualiza en el render siguiente,
  // y dos clics seguidos entran antes); el estado enciende el spinner del botón del modal.
  const [saving, setSaving] = useState<boolean>(false);
  const guardandoRef = useRef<boolean>(false);

  const { sidebarOpen, toggleSidebar } = useSidebar();

  // Search (debounced). Se recuerda lo TIPEADO; `searchTerm` es derivado.
  const [searchInput, setSearchInput] = useFiltroPersistido<string>(
    "configuracion.busquedaAdministradores",
    "",
    { validar: (v) => typeof v === "string" },
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setSearchTerm(searchInput.trim().toLowerCase());
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    fetchAdmins();
  }, []);

  useEffect(() => {
    if (!modalVisible) return;
    form.resetFields();
    if (!editingRecord) return;
    form.setFieldsValue({
      nombre_usuario: editingRecord.nombre_usuario,
      email_usuario: editingRecord.email_usuario,
      telefono_usuario: editingRecord.telefono_usuario,
    });
  }, [modalVisible, editingRecord, form]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await adminApi.listarAdministradores();
      if (res?.success) {
        setAdmins(res.data || []);
      }
    } catch (e) {
      console.error("Error al obtener administradores", e);
      getMsg()?.error("Error al cargar administradores");
    } finally {
      setLoading(false);
    }
  };

  const showModal = (record?: AdminUser) => {
    if (record) {
      setEditingRecord(record);
      // El modal ya se titula «Editar superadministrador» cuando corresponde (round 2026-10-31): el aviso breve debe decir lo mismo.
      getMsg()?.info(normalizeRole(record.rol) === "superadministrador" ? "Editando superadministrador" : "Editando administrador");
    } else {
      setEditingRecord(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  // El rechazo del servidor por el teléfono (409 duplicado, 400 formato) se marca además junto al campo.
  const alFallar = (error: ErrorApi) => {
    marcarCampoTelefono(form, "telefono_usuario", error);
  };

  const handleSubmit = async () => {
    // El candado se toma ANTES de validar: `validateFields` es asíncrono y dos clics seguidos entrarían los dos.
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    try {
      let values: any;
      try {
        values = await form.validateFields();
      } catch (errorValidacion) {
        // Los errores de cada campo ya quedaron marcados en el formulario; falta llevar a la persona hasta el primero (en el celular
        // el campo puede estar fuera de la pantalla y, sin esto, «Crear» no hacía NADA visible).
        irAlPrimerError(form, errorValidacion);
        return;
      }
      setSaving(true);

      if (editingRecord) {
        // EDICIÓN: nunca enviamos password aquí (la edición de admins
        // no soporta cambio de password — se hace por "recuperar").
        // Solo viaja lo que la persona CAMBIÓ respecto de la fila que vio al abrir el modal: si otra persona cambió el teléfono
        // mientras tanto y acá solo se tocó el nombre, el teléfono viejo del formulario no pisa el nuevo.
        const cambiados = clavesCambiadas(editingRecord as unknown as Record<string, unknown>, values, [
          "nombre_usuario",
          "email_usuario",
          "telefono_usuario",
        ]);
        if (cambiados.length === 0) {
          getMsg()?.info("No hay cambios que guardar.");
          setModalVisible(false);
          return;
        }
        const editValues: Record<string, unknown> = {};
        for (const k of cambiados) {
          // Vaciar el teléfono se manda como null (explícito); nunca se borra por un valor que no se entiende.
          editValues[k] = k === "telefono_usuario" ? telefonoParaGuardar(values[k]) : values[k];
        }
        const res = await adminApi.actualizarAdministrador(editingRecord.id, editValues, { alFallar });
        if (res) {
          getMsg()?.success(
            normalizeRole(editingRecord.rol) === "superadministrador"
              ? "Superadministrador actualizado correctamente."
              : "Administrador actualizado correctamente.",
          );
          setModalVisible(false);
          await fetchAdmins();
        }
      } else {
        // CREACIÓN — Política Opción B (ver docs/auth-roles.md):
        // - Si el superadmin tipeó password → se envía al backend y
        //   se usa esa exacta. El backend NO devuelve generatedPassword,
        //   así que no abrimos el modal de "contraseña generada"
        //   (el superadmin ya la conoce, y además le llegó al admin
        //   por correo).
        // - Si dejó vacío → backend autogenera y devuelve la generada;
        //   abrimos el modal para que el superadmin pueda copiarla.
        const adminTypedPassword =
          typeof values.password === "string" && values.password.trim() !== "";
        if (!adminTypedPassword) delete values.password;

        const res = await adminApi.crearAdministrador(
          { ...values, telefono_usuario: telefonoParaGuardar(values.telefono_usuario) },
          { alFallar },
        );
        if (res) {
          getMsg()?.success(
            adminTypedPassword
              ? "Administrador creado. Se envió un correo de bienvenida con la contraseña que ingresaste."
              : "Administrador creado. Se generó una contraseña temporal y se envió por correo."
          );
          setModalVisible(false);

          if (typeof res?.generatedPassword === "string" && res.generatedPassword.trim() !== "") {
            setGeneratedPassword(res.generatedPassword);
          }

          await fetchAdmins();
        }
      }
    } catch (error) {
      console.error(error);
      getMsg()?.error("Error al guardar.");
    } finally {
      guardandoRef.current = false;
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await adminApi.eliminarAdministrador(id);
      if (res) {
        getMsg()?.success("Administrador eliminado correctamente.");
        await fetchAdmins();
      }
    } catch (error) {
      console.error("Error al eliminar:", error);
      getMsg()?.error("Error al eliminar.");
    }
  };

  const filteredAdmins = useMemo(() => {
    const t = normalize(searchTerm);
    return (admins || []).filter((u) =>
      matches(t, u.nombre_usuario, u.email_usuario),
    );
  }, [admins, searchTerm]);

  // Usuarios del equipo que comparten teléfono, sobre la lista COMPLETA (un filtro de búsqueda no esconde un repetido).
  const gruposTelefonoRepetido = useMemo(
    () =>
      encontrarTelefonosRepetidos(
        (admins || []).map((u) => ({ id: u.id, nombre: u.nombre_usuario, telefono: u.telefono_usuario, rol: u.rol })),
      ),
    [admins],
  );

  return {
    activeTab,
    setActiveTab,
    admins: filteredAdmins,
    gruposTelefonoRepetido,
    saving,
    loading,
    modalVisible,
    editingRecord,
    generatedPassword,
    setGeneratedPassword,
    form,
    sidebarOpen,
    toggleSidebar,
    searchInput,
    setSearchInput,
    showModal,
    handleCancel,
    handleSubmit,
    handleDelete,
  };
};
